import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import {
	addLinkAssetDocument,
	removeDocumentLink,
} from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useAssetDocumentsGridActionsAndButtons = ({
	gridRef,
	searchStatistics,
	searchAssetDocuments,
	setRemoveDocumentError,
	includeDescendants,
}: {
	gridRef: GridApi | null;
	searchStatistics: Function;
	searchAssetDocuments: Function;
	setRemoveDocumentError: (err: string) => void;
	includeDescendants: boolean;
}) => {
	const {
		state: { assetSearchId, listAllDocuments, listAssetDocuments, rowSelected },
		actions: {
			setFirstTime,
			setDocumentSearch,
			setDataDocumentSelected,
			setListAssetDocuments,
		},
	} = useAssetDetailsContext();
	const [searchParams] = useSearchParams();
	const [refreshTrigger, setRefreshTrigger] = useState(1);
	const isMountRef = useRef(true);

	// BUTTONS STATES
	const [saveButton, setSaveButton] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);

	const refresh = useCallback(() => {
		if (!isMountRef.current) {
			setFirstTime(true);
			setSaveButton(false);
			setDeleteOpen(false);
			setDocumentSearch("");
			setDataDocumentSelected(undefined);
			searchAssetDocuments(includeDescendants);
			searchStatistics();
		}
	}, [searchParams, assetSearchId, includeDescendants]);

	const addRow = useCallback(() => {
		setSaveButton(true);
		const newData = { id: -1, AssetId: assetSearchId };
		const currentData = listAssetDocuments ? listAssetDocuments : [];
		const data = [newData, ...currentData];
		setListAssetDocuments(data);
	}, [listAssetDocuments, assetSearchId]);

	const removeSelected = useCallback(async () => {
		if (listAssetDocuments) {
			const selectedRowNodes = gridRef?.getSelectedNodes();
			try {
				if (selectedRowNodes?.length) {
					const removePromises = selectedRowNodes.map(async (documentLink) => {
						const removeDocument = await removeDocumentLink(
							assetSearchId as any,
							documentLink.data.DocumentName,
						);
						if (!removeDocument.ok) {
							setRemoveDocumentError(removeDocument.message);
							throw new Error(removeDocument.message);
						}
						return removeDocument;
					});

					await Promise.all(removePromises);
				} else if (rowSelected) {
					const removeDocument = await removeDocumentLink(
						assetSearchId as any,
						rowSelected.DocumentName,
					);
					if (!removeDocument.ok) {
						setRemoveDocumentError(removeDocument.message);
						return;
					}
				}

				setRefreshTrigger((prev) => prev + 1);
			} catch (error) {
				console.error("Error removing documents:", error);
			}
		}
	}, [listAllDocuments, assetSearchId, refresh, gridRef, rowSelected]);

	useEffect(() => {
		refresh();
	}, [refreshTrigger]);

	useEffect(() => {
		isMountRef.current = false;
	}, []);

	return {
		refresh,
		addRow,
		removeSelected,
		saveButton,
		deleteOpen,
		setSaveButton,
		setDeleteOpen,
	};
};
